/* OpenCSN Reference Design System Styles */
:root {
  /* Typography Tokens (self-hosted variable fonts) */
  --font-serif: "Lora Variable", "Lora", Georgia, "Times New Roman", serif;
  --font-sans: "Inter Variable", "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  --font-mono: "JetBrains Mono Variable", "JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace;

  /* Fluid Type Scale (clamp & rem only) */
  --text-hero: clamp(2.5rem, 1.2rem + 5.2vw, 5.25rem);
  --text-h1: clamp(2.125rem, 1.3rem + 3vw, 3.75rem);
  --text-h2: clamp(1.75rem, 1.2rem + 2vw, 2.75rem);
  --text-h3: clamp(1.25rem, 1.05rem + 1vw, 1.75rem);
  --text-hero-sub: clamp(1.125rem, 1rem + 0.6vw, 1.375rem);
  --text-body: clamp(1.0625rem, 1rem + 0.3vw, 1.1875rem);
  --text-ui: clamp(0.9375rem, 0.9rem + 0.2vw, 1rem);
  --text-ui-sm: clamp(0.8125rem, 0.78rem + 0.15vw, 0.875rem);
  --text-code: clamp(0.8125rem, 0.78rem + 0.2vw, 0.9375rem);

  /* Reading Measure Constraints (60 to 75 characters) */
  --measure-narrow: 56ch;
  --measure-prose: 68ch;
  --measure-wide: 75ch;

  /* Theme Weight for Lora (450 in dark mode for optimal legibility) */
  --font-weight-body: 450;

  /* Palette */
  --bg-main: #12100e;
  --bg-card: rgba(27, 23, 20, 0.85);
  --bg-card-solid: #1a1613;
  --bg-inset: rgba(0, 0, 0, 0.45);
  --bg-pill: rgba(255, 255, 255, 0.05);
  --text-main: #F6EEE7;
  --text-sec: #D3C6BB;
  --text-mut: #A99B90;
  --border-subtle: rgba(255, 255, 255, 0.08);
  --border-strong: rgba(255, 255, 255, 0.16);
  --copper: #c87a51;
  --copper-light: #df956d;

  /* WCAG AA Target Contrast Tokens */
  --text-primary: #F6EEE7;
  --text-secondary: #D3C6BB;
  --text-muted: #A99B90;
  --text-accent: #FFFFFF;
  --status-green: #4ADE9A;
  --status-amber: #F7C04A;
  --status-red: #F4795C;
  --status-card-bg: rgba(22, 15, 11, 0.88);
  --status-card-border: rgba(255, 255, 255, 0.08);
}

html.light {
  --font-weight-body: 400;
  --bg-main: #f6f2ec;
  --bg-card: rgba(255, 252, 247, 0.95);
  --bg-card-solid: #ffffff;
  --bg-inset: rgba(27, 23, 20, 0.04);
  --bg-pill: rgba(27, 23, 20, 0.05);
  --text-main: #1b1714;
  --text-sec: #5c534a;
  --text-mut: #8c8277;
  --border-subtle: rgba(27, 23, 20, 0.1);
  --border-strong: rgba(27, 23, 20, 0.2);
  --copper: #b8663c;
  --copper-light: #9e5028;
}

html {
  font-size: 100%;
  font-synthesis: none;
  text-rendering: optimizeLegibility;
  background: transparent;
}

#root {
  isolation: isolate;
  min-height: 100vh;
  background: transparent;
}

body {
  font-family: var(--font-sans);
  font-size: var(--text-ui);
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: transparent;
  color: var(--text-main);
  min-height: 100vh;
  overflow-x: hidden;
}

#app {
  position: relative;
  z-index: 1;
  background-color: transparent;
}

main, section {
  background-color: transparent;
}

/* Headings default to Lora serif with weight 500 */
h1, h2, h3 {
  font-family: var(--font-serif) !important;
  font-weight: 500 !important;
  font-synthesis: none !important;
  letter-spacing: -0.02em;
  overflow-wrap: break-word;
}

h1 {
  font-size: var(--text-h1);
  line-height: 1.12;
}

h2 {
  font-size: var(--text-h2);
  line-height: 1.15;
  letter-spacing: -0.015em;
}

h3 {
  font-size: var(--text-h3);
  line-height: 1.25;
  letter-spacing: -0.01em;
}

.hero-headline {
  font-family: var(--font-serif) !important;
  font-size: var(--text-hero) !important;
  font-weight: 500 !important;
  line-height: 1.08 !important;
  letter-spacing: -0.02em !important;
  font-synthesis: none !important;
  text-wrap: balance;
  overflow-wrap: break-word;
}

.hero-subtitle {
  font-family: var(--font-serif) !important;
  font-size: var(--text-hero-sub);
  font-weight: var(--font-weight-body);
  line-height: 1.5;
  max-width: var(--measure-narrow);
}

.serif-heading {
  font-family: var(--font-serif) !important;
  font-weight: 500;
  letter-spacing: -0.015em;
  line-height: 1.15;
  overflow-wrap: break-word;
}

.font-serif {
  font-family: var(--font-serif) !important;
}

.font-sans {
  font-family: var(--font-sans) !important;
}

.font-mono {
  font-family: var(--font-mono) !important;
}

.prose-serif {
  font-family: var(--font-serif);
  font-size: var(--text-body);
  font-weight: var(--font-weight-body);
  line-height: 1.65;
  max-width: var(--measure-prose);
}

/* Standarisasi ukuran font agar tidak ada teks yang terlalu kecil */
.text-\[9px\], .text-\[10px\], .text-\[11px\] {
  font-size: var(--text-ui-sm) !important;
  line-height: 1.4 !important;
}

input, select, textarea, button {
  font-family: inherit;
}

html.light body {
  background-color: transparent;
  background-image: none;
  color: var(--text-main);
}

html.light ::-webkit-scrollbar-thumb {
  background: rgba(27, 23, 20, 0.18);
}
html.light ::-webkit-scrollbar-thumb:hover {
  background: rgba(184, 102, 60, 0.65);
}

.surface-card {
  background-color: var(--bg-card);
  border: 1px solid var(--border-subtle);
  backdrop-filter: blur(12px);
}

.bg-inset { background-color: var(--bg-inset); }
.bg-pill { background-color: var(--bg-pill); }

.surface-card-solid {
  background-color: var(--bg-card-solid);
  border: 1px solid var(--border-subtle);
}

.status-surface-card {
  background-color: rgba(22, 15, 11, 0.88);
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
}

.footer-scrim {
  background: linear-gradient(to bottom, rgba(13, 9, 7, 0.55) 0%, rgba(13, 9, 7, 0.85) 100%);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

/* DESIGN LOCKED: matches the reference Status table. Do not change layout, spacing, columns, or sort order without an explicit design request. */
.status-grid-header {
  display: grid;
  grid-template-columns: minmax(180px, 1fr) 85px 85px 115px;
  height: 48px;
  align-items: center;
}

.status-grid-row {
  display: grid;
  grid-template-columns: minmax(180px, 1fr) 85px 85px 115px;
  min-height: 64px;
  height: 68px;
  align-items: center;
}

@media (min-width: 768px) {
  .status-grid-header {
    grid-template-columns: minmax(220px, 1.2fr) minmax(240px, 1.4fr) 90px 90px 120px;
    height: 48px;
  }
  .status-grid-row {
    grid-template-columns: minmax(220px, 1.2fr) minmax(240px, 1.4fr) 90px 90px 120px;
    min-height: 64px;
    height: 68px;
  }
}

@media (prefers-reduced-motion: reduce) {
  *, ::before, ::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}

.text-main { color: var(--text-main); }
.text-sec { color: var(--text-sec); }
.text-mut { color: var(--text-mut); }
.border-subtle { border-color: var(--border-subtle); }
.border-strong { border-color: var(--border-strong); }
.divide-subtle > :not([hidden]) ~ :not([hidden]) { border-color: var(--border-subtle); }

/* Global & Video-Accurate Custom Scrollbars */
* {
  scrollbar-width: thin;
  scrollbar-color: rgba(223, 149, 109, 0.35) transparent;
}
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 9999px;
}
::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.16);
  border-radius: 9999px;
  transition: background-color 0.15s ease;
}
::-webkit-scrollbar-thumb:hover {
  background: rgba(223, 149, 109, 0.7);
}
::-webkit-scrollbar-thumb:active {
  background: #df956d;
}

/* Custom Independent Scrollbar with clear boundary limits */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(223, 149, 109, 0.35) transparent;
  overscroll-behavior: contain;
}
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 9999px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.18);
  border-radius: 9999px;
  transition: background-color 0.15s ease;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(223, 149, 109, 0.75);
}
.custom-scrollbar::-webkit-scrollbar-thumb:active {
  background: #df956d;
}

html.light .custom-scrollbar {
  scrollbar-color: rgba(184, 102, 60, 0.55) transparent;
}
html.light .custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
html.light .custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(184, 102, 60, 0.45);
}
html.light .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(184, 102, 60, 0.85);
}

/* Line icons (lucide-style) */
.ico, :where(.ico), svg.ico { width: 1em; height: 1em; display: inline-block !important; vertical-align: -0.16em; flex-shrink: 0; }

/* Auto-scrolling marquee (Providers & Tools) */
.marquee {
  -webkit-mask-image: linear-gradient(to right, transparent 0, #000 7%, #000 93%, transparent 100%);
  mask-image: linear-gradient(to right, transparent 0, #000 7%, #000 93%, transparent 100%);
  touch-action: pan-y;
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}
.marquee-track {
  animation: marquee-scroll 30s linear infinite;
  animation-play-state: running;
  will-change: transform;
}
.marquee.is-paused .marquee-track { animation-play-state: paused; }
.marquee.is-paused { cursor: grabbing; }
@keyframes marquee-scroll {
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
}
@media (prefers-reduced-motion: reduce) {
  .marquee-track { animation: none; }
}

.st0 { fill: #72716d; }
.st1 { fill: #55544f; }
.st2 { fill: #fff; }
.st3 { fill: #43413c; }
.st4 { fill: #d6d5d2; }

@media (prefers-color-scheme: light) { :root { filter: none; } }
@media (prefers-color-scheme: dark) { :root { filter: none; } }

/* Circular View Transition for Theme Toggle */
::view-transition-old(root),
::view-transition-new(root) {
  animation: none;
  mix-blend-mode: normal;
}

::view-transition-old(root) {
  z-index: 1;
}

::view-transition-new(root) {
  z-index: 9999;
}

/* Fallback smooth transition when View Transition API is not active */
html.theme-transitioning,
html.theme-transitioning *,
html.theme-transitioning *::before,
html.theme-transitioning *::after {
  transition: background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease, fill 0.3s ease !important;
  transition-delay: 0s !important;
}

/* Rotating Provider Logo Carousel next to Jelajahi model */
.hero-model-icon-carousel {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.125rem;
  height: 1.125rem;
  overflow: hidden;
  vertical-align: middle;
}

.hero-model-icon-track {
  display: flex;
  flex-direction: column;
  animation: heroIconCycle 14.4s cubic-bezier(0.65, 0, 0.35, 1) infinite;
}

.hero-model-icon-item {
  width: 1.125rem;
  height: 1.125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: opacity 0.2s ease;
}

@keyframes heroIconCycle {
  0%, 10% {
    transform: translateY(0);
  }
  12.5%, 22.5% {
    transform: translateY(-1.125rem);
  }
  25%, 35% {
    transform: translateY(-2.25rem);
  }
  37.5%, 47.5% {
    transform: translateY(-3.375rem);
  }
  50%, 60% {
    transform: translateY(-4.5rem);
  }
  62.5%, 72.5% {
    transform: translateY(-5.625rem);
  }
  75%, 85% {
    transform: translateY(-6.75rem);
  }
  87.5%, 97.5% {
    transform: translateY(-7.875rem);
  }
  100% {
    transform: translateY(-9rem);
  }
}

